import {
  Component,
  ElementRef,
  Input,
  OnChanges,
  Output,
  EventEmitter,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HighchartsChartModule } from 'highcharts-angular';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as Highcharts from 'highcharts';
import type { PredefinedChartData } from '../../shared/predefined-charts.types';
import { SENTIMENT_SANKEY_COLORS } from '../../shared/constant';
import {
  buildHighchartsGetSvgExportOptions,
  fitChartExportCanvas
} from '../../shared/chart-image-export.util';
import { NotificationService } from '../../services/notification.service';

// Import Highcharts modules for special chart types
import HighchartsSankey from 'highcharts/modules/sankey';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import HighchartsExportData from 'highcharts/modules/export-data';

// Initialize modules
HighchartsSankey(Highcharts);
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);
HighchartsExportData(Highcharts);

/** Escape text for HTML tooltips so values like "<UNKNOWN>" are not parsed as tags. */
function escapeHtmlForTooltip(text: string | number | undefined | null): string {
  if (text == null) return '';
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

// Set global Highcharts options to disable thousand separators
Highcharts.setOptions({
  lang: {
    thousandsSep: ''
  },
  tooltip: {
    pointFormat: '{point.name}: {point.y}'
  }
});

// Define color palettes for different chart types
const COLOR_PALETTES = {
  default: [
    '#7c3aed', '#3b82f6', '#06b6d4', '#10b981', '#84cc16',
    '#eab308', '#f97316', '#ef4444', '#ec4899', '#a855f7'
  ],
  warm: [
    '#dc2626', '#ea580c', '#f97316', '#fb923c', '#fbbf24',
    '#facc15', '#fde047', '#fef08a', '#fef3c7', '#fffbeb'
  ],
  cool: [
    '#0891b2', '#0ea5e9', '#38bdf8', '#7dd3fc', '#bae6fd',
    '#e0f2fe', '#f0f9ff', '#dbeafe', '#bfdbfe', '#93c5fd'
  ],
  pastel: [
    '#fca5a5', '#fdba74', '#fcd34d', '#fde047', '#bef264',
    '#86efac', '#6ee7b7', '#5eead4', '#67e8f9', '#93c5fd'
  ]
};

@Component({
  selector: 'app-predefined-chart',
  standalone: true,
  imports: [CommonModule, FormsModule, HighchartsChartModule, MatTooltipModule],
  templateUrl: './predefined-chart.component.html',
  styleUrls: ['./predefined-chart.component.scss']
})
export class PredefinedChartComponent implements OnChanges {
  @Input() chartData: PredefinedChartData | null = null;
  @Input() chartTitle = '';
  @Input() chartId = '';
  @Input() isLargeView = false;
  @Input() loading = false;
  @Input() errorMessage: string | null = null;
  @Input() colorPalette: 'default' | 'warm' | 'cool' | 'pastel' = 'default';
  @Input() enableAnimations = true;
  @Input() showGranularityDropdown = false;
  @Input() granularityOptions: { label: string; value: number }[] = [];
  @Input() selectedGranularityHours = 4;

  @Output() viewInLarge = new EventEmitter<string>();
  @Output() granularityChange = new EventEmitter<number>();

  get exportRootId(): string {
    return this.chartId ? `predefined-chart-${this.chartId}` : 'predefined-chart';
  }

  get showChartActions(): boolean {
    return !this.loading && !this.errorMessage && !!this.chartData && !this.chartData.NoDataFound;
  }

  constructor(
    private el: ElementRef<HTMLElement>,
    private notificationService: NotificationService
  ) {}

  // Highcharts options and chart ref for export
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  updateFlag = false;
  chartRef: Highcharts.Chart | null = null;

  // Animation settings
  private readonly animationDefaults = {
    duration: 800,
    easing: 'easeOutCubic' as const,
    defer: 0
  };

  // Hover animation settings
  private readonly hoverAnimationDefaults = {
    duration: 200,
    easing: 'easeOutCubic' as const
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['chartData'] ||
      changes['colorPalette'] ||
      changes['enableAnimations'] ||
      changes['isLargeView']
    ) {
      this.buildChartOptions();
    }
  }

  private getColorPalette(): string[] {
    return COLOR_PALETTES[this.colorPalette] || COLOR_PALETTES.default;
  }

  /** Smaller x-axis category labels for stacked area charts (date/time buckets). */
  private applyAreaChartXAxisLabelStyle(options: Highcharts.Options): void {
    const fontSize = this.isLargeView ? '10px' : '9px';
    const mergeXAxis = (xa: Highcharts.XAxisOptions): Highcharts.XAxisOptions => ({
      ...xa,
      labels: {
        ...(xa.labels || {}),
        style: {
          ...((xa.labels?.style as Highcharts.CSSObject) || {}),
          fontSize,
          fontFamily: 'Lexend, sans-serif',
          color: '#6b7280',
        },
      },
    });

    const xa = options.xAxis;
    if (Array.isArray(xa)) {
      options.xAxis = xa.map(mergeXAxis);
    } else {
      options.xAxis = mergeXAxis(xa || {});
    }
  }

  /**
   * Stacked area charts: legend only in “view in large”; grid cards stay compact without legend.
   */
  private getAreaChartLegendOptions(isLargeView: boolean): Highcharts.LegendOptions {
    const itemWidth = isLargeView ? 252 : 142;
    const maxLegendHeight = 200;
    const navFontSize = isLargeView ? '13px' : '12px';
    const navArrow = isLargeView ? 16 : 14;

    return {
      enabled: isLargeView,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      margin: 12,
      padding: 8,
      itemDistance: 8,
      itemMarginTop: 4,
      itemMarginBottom: 4,
      symbolWidth: 12,
      symbolHeight: 10,
      symbolRadius: 2,
      squareSymbol: false,
      useHTML: false,
      itemWidth,
      maxHeight: maxLegendHeight,
      navigation: {
        enabled: true,
        arrowSize: navArrow,
        activeColor: '#7c3aed',
        inactiveColor: '#9ca3af',
        style: {
          fontSize: navFontSize,
          fontWeight: 'bold',
          color: '#6b7280',
        },
      },
      itemStyle: {
        fontSize: '12px',
        fontFamily: 'Lexend, sans-serif',
        color: '#374151',
        fontWeight: '500',
        cursor: 'pointer',
      },
      itemHoverStyle: {
        color: '#7c3aed',
      },
      itemHiddenStyle: {
        color: '#d1d5db',
      },
    };
  }

  private applyColorsAndAnimations(options: Highcharts.Options): void {
    const colors = this.getColorPalette();

    // Apply colors to chart
    options.colors = colors;

    // Disable exporting menu (hamburger icon)
    options.exporting = {
      enabled: false
    };

    // Disable context menu
    options.chart = {
      ...options.chart,
      animation: this.enableAnimations ? this.animationDefaults : undefined
    };

    const chartType = this.chartData?.ChartType?.toLowerCase() || '';
    if (chartType === 'area') {
      options.legend = {
        ...(options.legend || {}),
        ...this.getAreaChartLegendOptions(this.isLargeView),
      };
      options.chart = {
        ...options.chart,
        spacingBottom: this.isLargeView ? 20 : 8,
      };
    }

    // Apply animations
    if (this.enableAnimations) {
      // Apply plot options animations
      options.plotOptions = {
        ...options.plotOptions,
        series: {
          ...options.plotOptions?.series,
          animation: this.animationDefaults,
          states: {
            hover: {
              enabled: true,
              animation: this.hoverAnimationDefaults,
              brightness: 0.08,
              halo: {
                size: 15,
                opacity: 0.3,
                attributes: {
                  fill: 'currentColor'
                }
              }
            },
            select: {
              animation: {
                duration: 150,
                easing: 'easeOutCubic' as const
              },
              color: 'inherit'
            }
          }
        }
      };

      // Chart type specific animations (pie slice labels are set in buildChartOptions — SVG + {point.name} breaks "<UNKNOWN>" etc.)
      if (options.plotOptions?.pie) {
        options.plotOptions.pie = {
          ...options.plotOptions.pie,
          animation: this.animationDefaults,
          allowPointSelect: true,
        };
      }

      if (options.plotOptions?.column) {
        options.plotOptions.column = {
          ...options.plotOptions.column,
          animation: this.animationDefaults,
          pointPadding: 0.1,
          borderWidth: 0,
          borderRadius: 4,
          groupPadding: 0.1,
          states: {
            hover: {
              brightness: 0.08,
              halo: {
                size: 12,
                opacity: 0.25
              }
            }
          }
        };
      }

      if (options.plotOptions?.bar) {
        options.plotOptions.bar = {
          ...options.plotOptions.bar,
          animation: this.animationDefaults,
          pointPadding: 0.1,
          borderWidth: 0,
          borderRadius: 4,
          groupPadding: 0.1,
          states: {
            hover: {
              brightness: 0.08,
              halo: {
                size: 12,
                opacity: 0.25
              }
            }
          }
        };
      }

      if (options.plotOptions?.area) {
        options.plotOptions.area = {
          ...options.plotOptions.area,
          animation: this.animationDefaults,
          fillOpacity: 0.7,
          lineWidth: 2,
          marker: {
            enabled: true,
            radius: 4,
            states: {
              hover: {
                enabled: true,
                radius: 6
              }
            }
          }
        };
      }
    }
  }

  /** Expanded “view in large” layout: use container width and taller plot. */
  /**
   * Pie/donut data labels: API format uses {point.name} in SVG, so names like "<UNKNOWN>" disappear as invalid XML.
   * useHTML + escaped names matches tooltip behavior; honor MetricType for count vs percentage.
   */
  private applyPieDonutDataLabels(options: Highcharts.Options, d: PredefinedChartData): void {
    const isPercentage = d.MetricType === 'percentage';
    const prevPie = ((options.plotOptions || {}) as { pie?: Highcharts.PlotPieOptions }).pie || {};
    const rawLabels = (prevPie.dataLabels || {}) as Record<string, unknown>;
    const { format: _omitFormat, formatter: _omitFormatter, ...restLabels } = rawLabels;

    options.plotOptions = {
      ...options.plotOptions,
      pie: {
        ...prevPie,
        allowPointSelect: true,
        dataLabels: {
          ...restLabels,
          enabled: (rawLabels as { enabled?: boolean }).enabled !== false,
          useHTML: true,
          distance: (rawLabels as { distance?: number }).distance ?? 30,
          connectorWidth: (rawLabels as { connectorWidth?: number }).connectorWidth ?? 1,
          connectorColor: (rawLabels as { connectorColor?: string }).connectorColor ?? '#666666',
          style: {
            fontSize: '12px',
            fontWeight: 'bold',
            color: '#000000',
            ...((rawLabels as { style?: Highcharts.CSSObject }).style || {}),
          },
          animation: this.enableAnimations ? this.animationDefaults : undefined,
          formatter: function (this: Highcharts.PointLabelObject) {
            const name = escapeHtmlForTooltip(String(this.point?.name ?? ''));
            if (isPercentage) {
              const pct = this.percentage;
              const pctStr =
                typeof pct === 'number' && !isNaN(pct)
                  ? pct.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                      useGrouping: false,
                    }) + '%'
                  : '';
              return `${name}: ${pctStr}`;
            }
            const y = this.y;
            const yStr =
              typeof y === 'number'
                ? y.toLocaleString(undefined, { maximumFractionDigits: 0, useGrouping: false })
                : String(y ?? '');
            return `${name}: ${yStr}`;
          },
        },
      },
    };
  }

  private applyLargeViewChartLayout(options: Highcharts.Options, chartType: string): void {
    const chart = { ...(options.chart || {}) } as Highcharts.ChartOptions & { width?: unknown; height?: unknown };
    delete chart.width;
    delete chart.height;

    const height =
      chartType === 'sankey'
        ? 580
        : ['pie', 'donut'].includes(chartType)
          ? 460
          : chartType === 'area'
            ? 600
            : 520;

    options.chart = {
      ...chart,
      reflow: true,
      height,
    };
  }

  private buildChartOptions(): void {
    const d = this.chartData;
    if (!d || d.NoDataFound) {
      this.chartOptions = {};
      return;
    }

    // Handle specific chart types that need special formatting
    const chartType = d.ChartType?.toLowerCase() || '';

    // Start with the ChartConfig from API
    const cfg = d.ChartConfig as Highcharts.Options;
    const options: Highcharts.Options = {
      ...cfg,
      // Ensure title is empty (shown in HTML header)
      title: {
        text: ''
      },
      // Ensure credits are disabled
      credits: {
        enabled: false
      },
      // API ChartConfig may set lang; keep no thousands separator in tooltips/axes
      lang: {
        ...(cfg.lang || {}),
        thousandsSep: ''
      }
    };

    // Apply colors and animations
    this.applyColorsAndAnimations(options);

    if (this.isLargeView) {
      this.applyLargeViewChartLayout(options, chartType);
    }

    const appendPercentInTooltip = d.MetricType === 'percentage';

    // Add enhanced tooltip with better animation and styling
    options.tooltip = {
      ...options.tooltip,
      backgroundColor: 'rgba(255, 255, 255, 0.98)',
      borderColor: 'rgba(124, 58, 237, 0.2)',
      borderRadius: 12,
      borderWidth: 1,
      shadow: {
        color: 'rgba(0, 0, 0, 0.15)',
        offsetX: 0,
        offsetY: 4,
        opacity: 0.8,
        width: 8
      },
      style: {
        fontSize: '13px',
        fontFamily: 'Lexend, sans-serif',
        color: '#1f2937',
        fontWeight: '500',
        padding: '12px'
      },
      animation: this.enableAnimations,
      useHTML: true,
      followPointer: true,
      followTouchMove: true,
      hideDelay: 100,
      distance: 16,
      padding: 12,
      formatter: function () {
        const point = this.point || this;
        const seriesName = point.series ? point.series.name : '';
        const value = point.y !== undefined ? point.y : (point as any).value || (point as any).weight;
        const categoryName = point.category || point.name || '';
        const fromName = (point as any).from || (point as any).fromNode?.name || '';
        const toName = (point as any).to || (point as any).toNode?.name || '';
        const ptAny = point as any;
        const linkPercentRaw =
          ptAny.percent ??
          ptAny.options?.percent ??
          (typeof ptAny.options === 'object' && ptAny.options ? (ptAny.options as any).percent : undefined);
        const linkPercent =
          typeof linkPercentRaw === 'number' && !isNaN(linkPercentRaw)
            ? linkPercentRaw
            : linkPercentRaw != null && !isNaN(Number(linkPercentRaw))
              ? Number(linkPercentRaw)
              : undefined;

        let valueDisplay: string;
        if (chartType === 'sankey' && linkPercent !== undefined) {
          valueDisplay =
            linkPercent.toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
              useGrouping: false,
            }) + '%';
        } else if (typeof value === 'number') {
          valueDisplay = `${value.toLocaleString(undefined, {
            maximumFractionDigits: 2,
            useGrouping: false,
          })}${appendPercentInTooltip ? '%' : ''}`;
        } else {
          valueDisplay = String(value ?? '');
        }

        // Skip showing series name if it's "Series 1" or matches chart ID pattern
        const shouldShowSeriesName = seriesName &&
                                   seriesName !== 'Series 1' &&
                                   !seriesName.startsWith('HIGHCHARTS-') &&
                                   seriesName !== categoryName;

        // Create more attractive HTML tooltip
        return `
          <div style="
            background: linear-gradient(135deg, #ffffff 0%, #f9fafb 100%);
            border: 1px solid rgba(124, 58, 237, 0.15);
            border-radius: 12px;
            padding: 12px 16px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            min-width: 180px;
            backdrop-filter: blur(10px);
          ">
            ${fromName && toName ? `
              <div style="
                font-size: 11px;
                color: #6b7280;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 4px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
              ">${escapeHtmlForTooltip(fromName)} → ${escapeHtmlForTooltip(toName)}</div>
            ` : categoryName && categoryName !== fromName && categoryName !== toName ? `
              <div style="
                font-size: 11px;
                color: #6b7280;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 4px;
              ">${escapeHtmlForTooltip(categoryName)}</div>
            ` : ''}
            ${shouldShowSeriesName ? `
              <div style="
                font-size: 12px;
                color: #4b5563;
                font-weight: 500;
                margin-bottom: 6px;
              ">${escapeHtmlForTooltip(seriesName)}</div>
            ` : ''}
            <div style="
              font-size: 16px;
              color: #1f2937;
              font-weight: 700;
              display: flex;
              align-items: center;
              gap: 8px;
            ">
              <span style="
                display: inline-block;
                width: 8px;
                height: 8px;
                background: ${point.color || '#7c3aed'};
                border-radius: 50%;
                box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
              "></span>
              ${valueDisplay}
            </div>
          </div>
        `;
      }
    };

    if (['pie', 'donut'].includes(chartType)) {
      // For pie/donut charts, create a single series with [name, value] pairs
      const pieData = d.Categories?.map((category: string, index: number) => {
        const matchingSeries = d.Series?.find((s: any) => s.name === category);
        const value = matchingSeries ? matchingSeries.data[0] : 0;
        return [category, value];
      }) || [];

      options.series = [{
        name: (d as any).Title || 'Data',
        type: 'pie' as any, // Always use 'pie' type, donut is created with innerSize
        data: pieData
      }] as any;

      this.applyPieDonutDataLabels(options, d);
    } else if (chartType === 'sankey') {
      // Resolve links/nodes from TableData, ChartConfig, or Series (API may use different shapes/casing)
      const tableData = d.TableData as any;
      const chartConfig = d.ChartConfig as any;
      const linksRaw =
        tableData?.links ??
        tableData?.Links ??
        tableData?.data?.links ??
        (Array.isArray(tableData?.data) ? tableData.data : null) ??
        (Array.isArray(tableData) ? tableData : null) ??
        chartConfig?.links ??
        chartConfig?.Links ??
        this.getSankeyLinksFromSeries(d.Series);
      const nodesRaw =
        tableData?.nodes ??
        tableData?.Nodes ??
        tableData?.data?.nodes ??
        chartConfig?.nodes ??
        chartConfig?.Nodes ??
        [];
      const sankeyData = this.normalizeSankeyLinks(linksRaw);
      const nodes = Array.isArray(nodesRaw) ? this.normalizeSankeyNodes(nodesRaw) : [];
      const isSentimentSankey = this.chartId === 'sankey-sentiment';
      if (isSentimentSankey) {
        options.colors = SENTIMENT_SANKEY_COLORS;
        const po = (options.plotOptions || {}) as any;
        options.plotOptions = {
          ...po,
          sankey: {
            ...po.sankey,
            colors: SENTIMENT_SANKEY_COLORS,
            linkOpacity: 0.5,
          },
        };
      } else {
        const po = (options.plotOptions || {}) as any;
        options.plotOptions = {
          ...po,
          sankey: {
            ...po.sankey,
            linkOpacity: 0.5,
          },
        };
      }
      options.series = [{
        type: 'sankey',
        keys: ['from', 'to', 'weight'],
        data: sankeyData,
        nodes,
        ...(isSentimentSankey && { colorByPoint: true, colors: SENTIMENT_SANKEY_COLORS }),
      } as any];
    } else {
      // For column/bar/area charts: ensure x-axis uses Categories when available
      if (d.Categories && d.Categories.length > 0) {
        options.xAxis = {
          ...(options.xAxis as any),
          categories: d.Categories
        };
      }

      if (d.Categories && d.Categories.length > 0 &&
          d.Series && d.Series.length > 0 &&
          d.Series.every((s: any) => Array.isArray(s.data) && s.data.length === 1)) {

        if (chartType === 'area') {
          // For area charts: one series per category; each series gets a full data array
          // with value at the correct category index so x-axis shows all categories
          const colors = this.getColorPalette();
          const numCategories = d.Categories!.length;
          options.series = d.Categories!.map((category: string, index: number) => {
            const matchingSeries = d.Series!.find((s: any) => s.name === category);
            const value = matchingSeries && Array.isArray(matchingSeries.data) && matchingSeries.data.length > 0
              ? Number(matchingSeries.data[0])
              : 0;
            const dataForCategory = new Array(numCategories).fill(0);
            dataForCategory[index] = value;
            return {
              name: category,
              type: 'area' as any,
              data: dataForCategory,
              color: colors[index % colors.length]
            };
          });
        } else {
          // For column/bar charts, create a single series with all categories and their values
          const seriesData = d.Categories.map((category: string) => {
            const matchingSeries = d.Series!.find((s: any) => s.name === category);
            return matchingSeries ? matchingSeries.data[0] : 0;
          });

          options.series = [{
            name: (d as any).Title || 'Data',
            type: chartType as any,
            data: seriesData
          }];
        }
      } else if (chartType === 'area' && d.Categories && d.Categories.length > 0 && d.Series?.length) {
        // Area chart with one or more series each having multiple data points: use Categories as x-axis
        const colors = this.getColorPalette();
        options.series = d.Series.map((s: any, idx: number) => ({
          name: s.name,
          type: 'area' as any,
          data: Array.isArray(s.data) ? s.data : [],
          color: colors[idx % colors.length]
        }));
      } else if (d.Series && d.Series.length > 0) {
        // Fallback: Use Series data directly when other conditions aren't met
        const colors = this.getColorPalette();
        options.series = d.Series.map((s: any, idx: number) => ({
          name: s.name,
          type: (chartType || 'column') as any,
          data: Array.isArray(s.data) ? s.data : [],
          color: colors[idx % colors.length]
        }));
      } else {
        // Final fallback: Create empty series to prevent chart errors
        console.warn(`Chart ${this.chartId} has no valid series data. ChartData:`, d);
        options.series = [{
          name: 'No Data',
          type: 'column' as any,
          data: []
        }];
      }
    }

    if (chartType === 'area') {
      this.applyAreaChartXAxisLabelStyle(options);
    }

    this.chartOptions = options;
    this.updateFlag = !this.updateFlag;
  }

  /**
   * Normalize Sankey links to Highcharts object format { from, to, weight, percent? }.
   * Preserves API `percent` on each flow for tooltips when MetricType is volume.
   */
  private normalizeSankeyLinks(links: unknown): Array<{ from: string; to: string; weight: number; percent?: number }> {
    if (!links || !Array.isArray(links)) return [];
    const mapped = links.map((link: any) => {
      if (Array.isArray(link) && link.length >= 3) {
        const from = String(link[0]);
        const to = String(link[1]);
        const weight = Number(link[2]) || 0;
        const row: { from: string; to: string; weight: number; percent?: number } = { from, to, weight };
        if (link.length >= 4 && link[3] !== undefined && link[3] !== null) {
          const p = Number(link[3]);
          if (!isNaN(p)) {
            row.percent = p;
          }
        }
        return row;
      }
      if (link && typeof link === 'object') {
        const from = link.from ?? link.From ?? link.source ?? '';
        const to = link.to ?? link.To ?? link.target ?? '';
        const weight = link.weight ?? link.Weight ?? link.value ?? link.Value ?? 0;
        const percentRaw = link.percent ?? link.Percent ?? link.percentage ?? link.Percentage;
        const row: { from: string; to: string; weight: number; percent?: number } = {
          from: String(from),
          to: String(to),
          weight: Number(weight) || 0,
        };
        if (percentRaw !== undefined && percentRaw !== null && !isNaN(Number(percentRaw))) {
          row.percent = Number(percentRaw);
        }
        return row;
      }
      return { from: '', to: '', weight: 0 };
    });
    return mapped.filter(
      (row) => row.from !== '' && row.to !== '' && Number(row.weight) > 0
    );
  }

  /** Normalize Sankey nodes to Highcharts format: { id: string }[] */
  private normalizeSankeyNodes(nodes: unknown): Array<{ id: string }> {
    if (!nodes || !Array.isArray(nodes)) return [];
    return nodes.map((node: any) => {
      if (node && typeof node === 'object') {
        const id = node.id ?? node.Id ?? node.name ?? node.Name ?? '';
        return { id: String(id) };
      }
      return { id: String(node) };
    }).filter((n) => n.id !== '');
  }

  /** Extract link array from Series when API returns links as series data */
  private getSankeyLinksFromSeries(series: unknown): unknown[] {
    if (!series || !Array.isArray(series)) return [];
    const first = series[0];
    if (first && typeof first === 'object' && Array.isArray((first as any).data)) {
      return (first as any).data;
    }
    return series;
  }

  private formatSeriesData(series: any[]): any[] {
    return series.map(s => ({
      name: s.name,
      type: s.type as any,
      data: s.data,
      color: s.color
    }));
  }

  private formatPieData(data: any[], categories?: string[]): any[] {
    if (!data || !Array.isArray(data)) return [];

    // If we have categories and single data points, map them
    if (categories && categories.length === data.length && data.every(d => typeof d === 'number')) {
      return categories.map((name, index) => [name, data[index]]);
    }

    // Otherwise return data as-is
    return data;
  }

  onViewInLarge(): void {
    if (this.chartId) {
      this.viewInLarge.emit(this.chartId);
    }
  }

  onGranularityChange(hours: number): void {
    this.granularityChange.emit(hours);
  }

  private processExportSVG(svg: string, processingNotification: any): void {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      processingNotification.dismiss();
      this.notificationService.error('Export failed', 'Could not create image.');
      return;
    }

    const img = new Image();
    const headerPx = 60;

    img.onload = () => {
      URL.revokeObjectURL(svgUrl);
      try {
        const { canvasWidth, canvasHeight, drawWidth, drawHeight } = fitChartExportCanvas(
          img.naturalWidth,
          img.naturalHeight,
          headerPx
        );
        canvas.width = canvasWidth;
        canvas.height = canvasHeight;

        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#333333';
        ctx.font = 'bold 24px Lexend, sans-serif';
        ctx.fillText(this.chartTitle || 'Chart', 50, 40);
        ctx.drawImage(img, 0, headerPx, drawWidth, drawHeight);

        canvas.toBlob((blob: Blob | null) => {
          if (blob) {
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${this.exportRootId}.png`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
            processingNotification.dismiss();
            this.notificationService.success('Chart export', 'Chart exported.');
          } else {
            processingNotification.dismiss();
            this.notificationService.error('Export failed', 'Could not create image.');
          }
        }, 'image/png');
      } catch (err) {
        console.error('Could not generate chart image', err);
        processingNotification.dismiss();
        this.notificationService.error('Export failed', 'Could not export chart. Please try again.');
      }
    };

    img.onerror = () => {
      URL.revokeObjectURL(svgUrl);
      processingNotification.dismiss();
      this.notificationService.error('Export failed', 'Could not generate chart image.');
    };

    const svgBlob = new Blob([svg], { type: 'image/svg+xml;charset=utf-8' });
    const svgUrl = URL.createObjectURL(svgBlob);
    img.src = svgUrl;
  }

  exportChart(): void {
    if (!this.chartRef) {
      this.notificationService.error('Export failed', 'Chart not ready for export.');
      return;
    }

    const processingNotification = this.notificationService.processing(
      'Exporting',
      'File is exporting...'
    );

    try {
      const chartType = this.chartData?.ChartType?.toLowerCase() || '';
      const exportOptions: Highcharts.Options = {
        ...buildHighchartsGetSvgExportOptions(this.chartOptions),
      };

      if (chartType === 'area' && this.chartOptions.legend) {
        exportOptions.legend = { ...this.chartOptions.legend, enabled: true };
        if (this.chartOptions.series) {
          exportOptions.series = [...this.chartOptions.series];
        }
        exportOptions.chart = {
          ...exportOptions.chart,
          width: this.chartOptions.chart?.width,
          height: this.chartOptions.chart?.height,
        };

        setTimeout(() => {
          const svg = this.chartRef!.getSVG(exportOptions);
          this.processExportSVG(svg, processingNotification);
        }, 100);
        return;
      }

      const svg = this.chartRef.getSVG(exportOptions);
      this.processExportSVG(svg, processingNotification);
    } catch (err) {
      console.error('Could not export chart', err);
      processingNotification.dismiss();
      this.notificationService.error('Export failed', 'Could not export chart. Please try again.');
    }
  }
}
